import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, can } from "@/context/AuthContext";
import { playOrderChime, unlockAudio, audioUnlocked } from "@/lib/alertSound";

const POLL_MS = 20000;
const CURSOR_KEY = "apc_store_orders_cursor";
const SEEN_KEY = "apc_store_orders_seen";
export const SOUND_KEY = "apc_store_orders_sound";

export const soundEnabled = () => localStorage.getItem(SOUND_KEY) !== "off";

const STATUS_COPY = {
  paid:      { title: "¡Pago aprobado en la tienda!", tone: "success" },
  pending:   { title: "Alguien está pagando en la tienda", tone: "warning" },
  rejected:  { title: "Pago rechazado — contacta al cliente", tone: "error" },
  cancelled: { title: "Pago cancelado en la tienda", tone: "error" },
};

const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const loadSeen = () => { try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set(); } };
const saveSeen = (set) => localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-100)));

// Vigila los pedidos de la tienda y avisa con sonido fuerte + toast + notificación.
export default function StoreOrderAlerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const seenRef = useRef(loadSeen());
  const allowed = !!user && can(user, "online_orders");

  useEffect(() => {
    if (!allowed) return undefined;
    const unlock = () => { unlockAudio(); };
    window.addEventListener("pointerdown", unlock, { passive: true });
    window.addEventListener("keydown", unlock);
    if ("Notification" in window && Notification.permission === "default") {
      const ask = () => { Notification.requestPermission().catch(() => {}); window.removeEventListener("pointerdown", ask); };
      window.addEventListener("pointerdown", ask, { passive: true });
    }
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return undefined;
    let stopped = false;
    let originalTitle = document.title;

    const announce = (ev) => {
      const copy = STATUS_COPY[ev.status] || { title: "Movimiento en pedidos de tienda", tone: "info" };
      const desc = [
        `${ev.customer_name}${ev.customer_phone ? ` · ${ev.customer_phone}` : ""}`,
        `${fmtMXN(ev.total_mxn)} · ${ev.items_count} art.${ev.first_item ? ` · ${ev.first_item}` : ""}`,
        ev.status_detail_label && ev.status !== "paid" ? ev.status_detail_label : null,
      ].filter(Boolean).join("\n");
      const fn = toast[copy.tone] || toast;
      fn(copy.title, {
        description: <span style={{ whiteSpace: "pre-line" }}>{desc}</span>,
        duration: 60000,
        action: { label: "Ir a pedidos", onClick: () => navigate("/online-orders") },
        id: `store-order-${ev.order_id}-${ev.status}`,
      });
      if ("Notification" in window && Notification.permission === "granted" && document.visibilityState !== "visible") {
        try { new Notification(copy.title, { body: desc.replace(/\n/g, " · "), tag: ev.order_id }); } catch { /* ignore */ }
      }
      if (document.visibilityState !== "visible") {
        originalTitle = originalTitle.replace(/^\(\d+\) 🔔 /, "");
        document.title = `(1) 🔔 ${originalTitle}`;
        const restore = () => { document.title = originalTitle; document.removeEventListener("visibilitychange", restore); };
        document.addEventListener("visibilitychange", restore);
      }
    };

    const tick = async () => {
      try {
        const since = localStorage.getItem(CURSOR_KEY);
        const { data } = await api.get("/online-orders/activity", { params: since ? { since } : {} });
        if (stopped) return;
        localStorage.setItem(CURSOR_KEY, data.server_time);
        const fresh = (data.events || []).filter((ev) => !seenRef.current.has(`${ev.order_id}:${ev.status}`));
        if (fresh.length === 0) return;
        fresh.forEach((ev) => seenRef.current.add(`${ev.order_id}:${ev.status}`));
        saveSeen(seenRef.current);
        fresh.slice(0, 3).forEach(announce);
        if (soundEnabled()) {
          const played = await playOrderChime({ repeats: fresh.some((e) => e.status === "paid") ? 4 : 3 });
          if (!played && !audioUnlocked()) {
            toast.message("Toca la pantalla para activar el sonido de pedidos", { id: "store-sound-unlock", duration: 8000 });
          }
        }
      } catch { /* silencioso: la próxima vuelta reintenta */ }
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stopped = true; clearInterval(id); };
  }, [allowed, navigate]);

  return null;
}
